import React, { createContext, useContext, useState, useEffect } from 'react'
import { supabase } from '../lib/supabase'
import { clearApolloCache } from '../services/api'

const AuthContext = createContext({})

export const useAuthContext = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuthContext must be used within an AuthProvider')
  }
  return context
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null)
  const [session, setSession] = useState(null)
  const [loading, setLoading] = useState(true)
  const [accessToken, setAccessToken] = useState(null)

  useEffect(() => {
    // Get initial session
    const getInitialSession = async () => {
      try {
        const { data: { session }, error } = await supabase.auth.getSession()
        if (error) {
          console.error('Error getting initial session:', error)
        } else {
          setSession(session)
          setUser(session?.user ?? null)
          setAccessToken(session?.access_token ?? null)
        }
      } catch (error) {
        console.error('Error in getInitialSession:', error)
      } finally {
        setLoading(false)
      }
    }

    getInitialSession()

    // Listen for auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event, session) => {
      console.log('Auth state changed:', event, session?.user?.email)
      
      setSession(session)
      setUser(session?.user ?? null)
      setAccessToken(session?.access_token ?? null)
      setLoading(false)

      // Clear Apollo cache when user signs out
      if (event === 'SIGNED_OUT') {
        clearApolloCache()
      }

      if (event === 'PASSWORD_RECOVERY') {
        // Redirect to dedicated reset page (AuthProvider is outside Router)
        try {
          window.location.replace('/auth/reset')
        } catch (_) {
          // fallback
          window.location.href = '/auth/reset'
        }
      }
    })

    return () => subscription.unsubscribe()
  }, [])

  const signOut = async () => {
    try {
      // Clear Apollo cache before signing out
      clearApolloCache()
      
      const { error } = await supabase.auth.signOut()
      if (error) {
        console.error('Error signing out:', error)
        return { error }
      }
      return { error: null }
    } catch (error) {
      console.error('Error during sign out process:', error)
      return { error }
    }
  }

  const value = {
    user,
    session,
    loading,
    accessToken,
    signOut,
    isAuthenticated: !!session,
    supabase, // Expose supabase client for direct access if needed
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
} 